import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Handshake, Plus, Trash2, ExternalLink, GripVertical, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function PartnersAdmin() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', logo: '', url: '', order: 0 });
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get('/partners').then(r => setPartners(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/rsx/upload-image', fd);
      setForm(f => ({ ...f, logo: r.data.url }));
      toast.success('Logo uploadé');
    } catch { toast.error('Erreur upload'); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name) { toast.error('Nom requis'); return; }
    try {
      if (editing) {
        await api.put(`/admin/partners/${editing}`, form);
        toast.success('Partenaire modifié');
      } else {
        await api.post('/admin/partners', form);
        toast.success('Partenaire ajouté');
      }
      setForm({ name: '', logo: '', url: '', order: 0 });
      setEditing(null);
      load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    await api.delete(`/admin/partners/${id}`);
    toast.success('Supprimé');
    load();
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, logo: p.logo, url: p.url || '', order: p.order || 0 });
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="partners-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Handshake className="w-6 h-6 text-[#D4AF37]" /> Partenaires
          </h1>
          <p className="text-sm text-[#A1A1AA]">Gérez les logos affichés sur la page d'accueil</p>
        </div>
      </div>

      {/* Formulaire d'ajout/édition */}
      <div className="bg-[#121212] border border-white/5 rounded-2xl p-5 mb-6" data-testid="partner-form">
        <h3 className="text-sm font-semibold text-[#D4AF37] mb-4" style={{ fontFamily: 'Outfit' }}>
          {editing ? 'Modifier le partenaire' : 'Ajouter un partenaire'}
        </h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs text-[#A1A1AA] mb-1 block">Nom *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4AF37]/50"
              placeholder="Miss Naturelle" data-testid="partner-name-input" />
          </div>
          <div>
            <label className="text-xs text-[#A1A1AA] mb-1 block">Site web (optionnel)</label>
            <input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#D4AF37]/50"
              placeholder="https://www.partenaire.com" data-testid="partner-url-input" />
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs text-[#A1A1AA] mb-1 block">Logo</label>
          <div className="flex items-center gap-4">
            {form.logo && (
              <div className="w-20 h-20 rounded-xl bg-[#0A0A0A] border border-white/10 p-2 flex items-center justify-center overflow-hidden">
                <img src={form.logo} alt="Logo" className="w-full h-full object-contain" />
              </div>
            )}
            <label className="cursor-pointer">
              <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-sm text-[#A1A1AA] hover:border-[#D4AF37]/30 transition-colors">
                <Upload className="w-4 h-4" /> {uploading ? 'Upload...' : 'Importer un logo'}
              </div>
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="partner-logo-upload" />
            </label>
            {form.logo && (
              <input value={form.logo} onChange={e => setForm(f => ({ ...f, logo: e.target.value }))}
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-xs outline-none text-[#555]"
                placeholder="URL du logo" />
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={save} className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-6" data-testid="partner-save-btn">
            {editing ? 'Modifier' : '+ Ajouter'}
          </Button>
          {editing && (
            <Button variant="outline" onClick={() => { setEditing(null); setForm({ name: '', logo: '', url: '', order: 0 }); }}
              className="border-white/10 rounded-full px-6">Annuler</Button>
          )}
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-3" data-testid="partners-list">
        {partners.length === 0 && <p className="text-center text-[#A1A1AA] py-10">Aucun partenaire. Ajoutez-en un !</p>}
        {partners.map(p => (
          <div key={p.id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`partner-item-${p.id}`}>
            <div className="w-16 h-16 rounded-xl bg-[#0A0A0A] border border-white/5 p-2 flex items-center justify-center overflow-hidden shrink-0">
              {p.logo ? <img src={p.logo} alt={p.name} className="w-full h-full object-contain" /> : <Handshake className="w-6 h-6 text-[#555]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{p.name}</div>
              {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D4AF37] flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" />{p.url}</a>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => edit(p)} className="border-white/10 rounded-full text-xs">Modifier</Button>
              <Button size="sm" variant="outline" onClick={() => remove(p.id)} className="border-red-500/20 text-red-400 rounded-full text-xs"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
